// web/src/components/ChatMessage.tsx
// Shared message component used by ChatPage and SharedConversationPage
import React from 'react';
import { User, MessageSquare } from 'react-feather';

export interface Source {
  url?: string;
  title?: string;
  score?: number;
  chunk?: {
    source?: {
      url?: string;
      title?: string;
    };
  };
}

export interface ChatMessageProps {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  // Optional metadata for assistant messages
  confidence?: number;
  responseTime?: number;
  sources?: Source[];
  responseId?: string;
  conversationId?: string;
  // Optional children for feedback/satisfaction components
  children?: React.ReactNode;
  // Display options
  showMetadata?: boolean;
  showSources?: boolean;
}

/**
 * Renders source links with deduplication and expand/collapse for >3 sources
 */
const renderSources = (sources: Source[], messageId: string): string => {
  // Deduplicate sources by URL
  const uniqueSources: Source[] = [];
  const seenUrls = new Set();

  for (const source of sources) {
    const url = source.url || source.chunk?.source?.url || '';
    if (url && !seenUrls.has(url)) {
      seenUrls.add(url);
      uniqueSources.push(source);
    }
  }

  if (uniqueSources.length === 0) return '';

  // Show first 3 unique sources
  const initialSources = uniqueSources.slice(0, 3).map((source, index) => {
    const url = source.url || source.chunk?.source?.url || '';
    const title = source.title || source.chunk?.source?.title || 'Unknown Source';
    const score = source.score ? ` (${source.score.toFixed(1)})` : '';
    return `<div class="source-item">${index + 1}. <a href="${url}" class="source-link" target="_blank">${title}</a>${score}</div>`;
  }).join('');

  // Additional unique sources (hidden initially)
  const additionalSources = uniqueSources.slice(3).map((source, index) => {
    const url = source.url || source.chunk?.source?.url || '';
    const title = source.title || source.chunk?.source?.title || 'Unknown Source';
    const score = source.score ? ` (${source.score.toFixed(1)})` : '';
    return `<div class="source-item">${index + 4}. <a href="${url}" class="source-link" target="_blank">${title}</a>${score}</div>`;
  }).join('');

  const moreCount = uniqueSources.length > 3 ? uniqueSources.length - 3 : 0;
  const expandLink = moreCount > 0 ?
    `<div class="source-item">
      <span class="expand-sources-link" onclick="window.expandSources && window.expandSources('${messageId}')">... and ${moreCount} more sources</span>
     </div>
     <div id="${messageId}_additional" class="additional-sources" style="display: none;">
        ${additionalSources}
     </div>` : '';

  return `<div class="message-sources">📚 Sources:${initialSources}${expandLink}</div>`;
};

/**
 * Renders metadata (confidence and response time)
 */
const renderMetadata = (confidence?: number, responseTime?: number): string => {
  const parts: string[] = [];

  if (confidence !== undefined) {
    const confidencePercent = (confidence * 100).toFixed(0);
    const confidenceIcon = confidence > 0.7 ? '🟢' : confidence > 0.4 ? '🟡' : '🔴';
    const confidenceClass = confidence > 0.8 ? 'high-confidence' : '';
    parts.push(`${confidenceIcon} <span class="${confidenceClass}">${confidencePercent}%</span>`);
  }

  if (responseTime !== undefined) {
    parts.push(`⚡ ${responseTime}ms`);
  }

  if (parts.length === 0) return '';
  return `<div class="message-metadata">${parts.join(' • ')}</div>`;
};

const ChatMessage: React.FC<ChatMessageProps> = ({
  id,
  role,
  content,
  confidence,
  responseTime,
  sources,
  responseId,
  conversationId,
  children,
  showMetadata = true,
  showSources = true,
}) => {
  const metadataHtml = showMetadata ? renderMetadata(confidence, responseTime) : '';
  const sourcesHtml = showSources && sources && sources.length > 0
    ? renderSources(sources, `sources_${id}`)
    : '';

  return (
    <div
      className={`message ${role === 'user' ? 'user' : 'assistant'}`}
      data-response-id={responseId}
      data-conversation-id={conversationId}
    >
      <div className="message-avatar">
        {role === 'user' ? (
          <User size={20} />
        ) : (
          <MessageSquare size={20} />
        )}
      </div>
      <div className="message-content">
        <div
          className="message-text"
          dangerouslySetInnerHTML={{
            __html: content + metadataHtml + sourcesHtml
          }}
        />
        {children}
      </div>
    </div>
  );
};

export default ChatMessage;
